import { useState } from 'preact/hooks';
import { requireSupabase } from './supabase.ts';

/**
 * Passwordless sign-in: an email address, then the six-digit code sent to it.
 *
 * `shouldCreateUser: false` is deliberate — accounts are created by hand in
 * the Supabase dashboard. Without it, anyone who found the URL could sign
 * themselves up and get an empty but valid account on this project.
 */
/** Supabase allows an OTP of 6 to 10 digits; the project decides which. */
const OTP_MAX_LENGTH = 10;

/**
 * Keep digits only.
 *
 * Codes are usually pasted straight out of a mail client, which tends to
 * bring a trailing space or a non-breaking space with it. Those make the code
 * fail verification for no visible reason.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '').slice(0, OTP_MAX_LENGTH);
}

export function LoginScreen() {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendCode(e: Event) {
    e.preventDefault();
    if (!email.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const { error: sendError } = await requireSupabase().auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: false },
      });
      if (sendError) throw sendError;
      setStep('code');
    } catch (err) {
      setError(
        err instanceof Error
          ? `Impossible d’envoyer le code : ${err.message}`
          : 'Impossible d’envoyer le code.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: Event) {
    e.preventDefault();
    if (!code.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const { error: verifyError } = await requireSupabase().auth.verifyOtp({
        email: email.trim(),
        token: code.trim(),
        type: 'email',
      });
      if (verifyError) throw verifyError;
      // The session listener in AuthGate takes over from here.
    } catch {
      setError('Code incorrect ou expiré.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="loginScreen">
      <div class="card loginCard">
        <div class="logo" style={{ marginBottom: '18px' }}>
          <div class="logoMark">JC</div>
          <span>Mon journal de classe</span>
        </div>

        {step === 'email' ? (
          <form onSubmit={(e) => void sendCode(e)}>
            <h2>Connexion</h2>
            <p class="muted" style={{ margin: '8px 0 16px' }}>
              Entre ton adresse e-mail : tu recevras un code par e-mail.
            </p>

            <label for="loginEmail">Adresse e-mail</label>
            <input
              id="loginEmail"
              type="email"
              autocomplete="email"
              required
              value={email}
              placeholder="prenom@exemple.be"
              onInput={(e) => setEmail(e.currentTarget.value)}
            />

            {error && <div class="loginError">{error}</div>}

            <button class="btn primary" type="submit" disabled={busy} style={{ marginTop: '14px' }}>
              {busy ? 'Envoi…' : 'Recevoir mon code'}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void verify(e)}>
            <h2>Code reçu</h2>
            <p class="muted" style={{ margin: '8px 0 16px' }}>
              Un code a été envoyé à <b>{email}</b>.
            </p>

            <label for="loginCode">Code reçu par e-mail</label>
            <input
              id="loginCode"
              inputMode="numeric"
              autocomplete="one-time-code"
              // Supabase's OTP length is a project setting, anywhere from 6 to
              // 10 digits. Hardcoding a length here silently truncates a
              // pasted code and leaves the user staring at a valid-looking
              // field that will not verify.
              maxLength={OTP_MAX_LENGTH}
              required
              value={code}
              placeholder="Ton code"
              onInput={(e) => setCode(digitsOnly(e.currentTarget.value))}
            />

            {error && <div class="loginError">{error}</div>}

            <div class="row" style={{ marginTop: '14px' }}>
              <button class="btn primary" type="submit" disabled={busy}>
                {busy ? 'Vérification…' : 'Me connecter'}
              </button>
              <button
                class="btn ghost"
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
              >
                Changer d’adresse
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
